import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';

import * as types from '@/core/store/mutation-types';
import { replaceAngularUrls } from './angular-url-mapping';
import { setAccessToken } from '@/core/api';
import store from '@/core/store';
import {
  trackFbPixel,
  identify,
  track,
  gaTrackPageView,
} from '@/web/services/analytics';
import AuthApi from '@/core/api/modules/auth';

const AdminTemplate = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/AdminTemplate.vue'
  );
const Categories = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/media-library/Categories.vue'
  );
const Instructors = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/media-library/Instructors.vue'
  );
const Library = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/media-library/Library.vue'
  );
const LibraryAddMedia = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/media-library/LibraryAddMedia.vue'
  );
const LibraryViewMedia = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/media-library/LibraryViewMedia.vue'
  );
const QuickStartGuide = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/QuickStartGuide.vue'
  );
const Dashboard = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/Dashboard.vue'
  );
const ShareAndEarn = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/ShareAndEarn.vue'
  );
const Elements = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/Elements.vue'
  );
const AdminLogin = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/AdminLogin.vue'
  );
const MeetingTypes = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/marketing/MeetingTypes.vue'
  );
const MeetingTypeEditor = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/marketing/MeetingTypeEditor.vue'
  );
const Emails = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/marketing/Emails.vue'
  );
const EmailDetail = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/marketing/EmailDetail.vue'
  );
const MarketingAssets = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/marketing/MarketingAssets.vue'
  );
const AnnouncementDetail = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/marketing/AnnouncementDetail.vue'
  );
const CustomerList = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/customers/CustomerList.vue'
  );
const CustomerDetail = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/customers/CustomerDetail.vue'
  );
const CustomerDetailActivity = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/customers/CustomerDetailActivity.vue'
  );
const CustomerDetailClassCard = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/customers/CustomerDetailClassCard.vue'
  );
const CustomerDetailProducts = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/customers/CustomerDetailProducts.vue'
  );
const CustomerDetailEvents = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/customers/CustomerDetailEvents.vue'
  );
const CustomerDetailEmails = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/customers/CustomerDetailEmails.vue'
  );
const CustomerDetailGroups = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/customers/CustomerDetailGroups.vue'
  );
const CustomerDetailFavorites = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/customers/CustomerDetailFavorites.vue'
  );
const CustomerDetailWaivers = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/customers/CustomerDetailWaivers.vue'
  );
const CustomerDetailNotes = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/customers/CustomerDetailNotes.vue'
  );
const CustomerEditNote = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/customers/CustomerEditNote.vue'
  );
const Reports = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/reports/Reports.vue'
  );
const ReportListByGroup = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/reports/ReportListByGroup.vue'
  );
const ReportDetail = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/reports/ReportDetail.vue'
  );
const Pages = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/pages/Pages.vue'
  );
const EditPage = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/pages/EditPage.vue'
  );
const PageSEO = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/pages/PageSEO.vue'
  );
const PageSettings = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/pages/PageSettings.vue'
  );
const AddPage = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/pages/AddPage.vue'
  );
const PageBuilder = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/pages/PageBuilder.vue'
  );
const Orders = () =>
  import(/* webpackChunkName: "group-admin" */ '@/web/views/admin/Orders.vue');
const TipJar = () =>
  import(/* webpackChunkName: "group-admin" */ '@/web/views/admin/TipJar.vue');
const GiftPurchases = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/GiftPurchases.vue'
  );
const Signup = () =>
  import(/* webpackChunkName: "group-admin" */ '@/web/views/admin/Signup.vue');
const CodeSignup = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/CodeSignup.vue'
  );
const AffiliateSignup = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/AffiliateSignup.vue'
  );
const BioSignup = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/BioSignup.vue'
  );
const BioUpgrade = () =>
  import(/* webpackChunkName: "group-admin" */ '@/web/views/BioUpgrade.vue');
const BioTopBar = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/bio/BioTopBar.vue'
  );
const Join = () =>
  import(/* webpackChunkName: "group-admin" */ '@/web/views/admin/Join.vue');
const ProgrammeSignup = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/ProgrammeSignup.vue'
  );
const LWIProgrammeSignup = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/LWIProgrammeSignup.vue'
  );
const ListLoveProgrammeSignup = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/ListLoveProgrammeSignup.vue'
  );
const MarvelousHolidayBoxProgrammeSignup = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/MarvelousHolidayBoxProgrammeSignup.vue'
  );
const InnerCircleProgrammeSignup = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/InnerCircleProgrammeSignup.vue'
  );
const InnerCircle4MonthsProgrammeSignup = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/InnerCircle4MonthsProgrammeSignup.vue'
  );
const MembershipMarvelousProgrammeSignup = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/MembershipMarvelousProgrammeSignup.vue'
  );
const ChallengeFunnelBlueprintProgrammeSignup = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/ChallengeFunnelBlueprintProgrammeSignup.vue'
  );
const MyProduct = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/products/MyProduct.vue'
  );
const Coupons = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/products/Coupons.vue'
  );
const Contracts = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/contracts/Contracts.vue'
  );
const EditContract = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/contracts/EditContract.vue'
  );
const Podcasts = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/podcasts/Podcasts.vue'
  );
const EditPodcast = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/podcasts/EditPodcast.vue'
  );
const PodcastEpisodes = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/podcasts/PodcastEpisodes.vue'
  );
const CouponDetail = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/coupons/CouponDetail.vue'
  );
const CreateCoupon = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/coupons/CreateCoupon.vue'
  );
const EditCoupon = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/coupons/EditCoupon.vue'
  );
const TaxRates = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/products/TaxRates.vue'
  );
const ProductTags = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/products/ProductTags.vue'
  );
const ProductDetail = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/products/ProductDetail.vue'
  );
const Quizzes = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/quizzes/Quizzes.vue'
  );
const Quiz = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/quizzes/Quiz.vue'
  );
const Sidebar = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/components/navigation/Sidebar.vue'
  );
const EditProduct = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/products/EditProduct.vue'
  );
const Unsubscribe = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/Unsubscribe.vue'
  );
const AdminResetPassword = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/AdminResetPassword.vue'
  );
const AdminGetResetPassword = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/AdminGetResetPassword.vue'
  );
const ThankYou = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/ThankYou.vue'
  );
const ThankYouBio = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/ThankYouBio.vue'
  );
const Account = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/Account.vue'
  );
const Branding = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/Branding.vue'
  );
const AdjustPlan = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/AdjustPlan.vue'
  );
const Settings = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/Settings.vue'
  );
const Billing = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/Billing.vue'
  );
const Integrations = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/Integrations.vue'
  );
const Widgets = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/Widgets.vue'
  );
const CodeEmbed = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/CodeEmbed.vue'
  );
const DataImport = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/DataImport.vue'
  );
const DataImportStudents = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/DataImportStudents.vue'
  );
const DataImportMedia = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/DataImportMedia.vue'
  );
const DataImportClassCards = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/DataImportClassCards.vue'
  );
const DataImportPurchases = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/DataImportPurchases.vue'
  );
const DataImportSubscriptions = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/DataImportSubscriptions.vue'
  );
const CalendlyIntegration = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/CalendlyIntegration.vue'
  );
const CalendlyIntegrationV2 = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/CalendlyIntegrationV2.vue'
  );
const AcuityIntegration = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/AcuityIntegration.vue'
  );
const FlodeskIntegration = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/FlodeskIntegration.vue'
  );
const ConvertkitIntegration = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/ConvertkitIntegration.vue'
  );
const MailchimpIntegration = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/MailchimpIntegration.vue'
  );
const TwilioIntegration = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/TwilioIntegration.vue'
  );
const SmtpIntegration = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/SmtpIntegration.vue'
  );
const PayPalIntegration = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/PayPalIntegration.vue'
  );
const StripeIntegration = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/StripeIntegration.vue'
  );
const FacebookPixelIntegration = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/FacebookPixelIntegration.vue'
  );
const GoogleAnalyticsIntegration = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/GoogleAnalyticsIntegration.vue'
  );
const DeadlineFunnelIntegration = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/DeadlineFunnelIntegration.vue'
  );
const ZapierIntegration = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/ZapierIntegration.vue'
  );
const RewardfulIntegration = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/RewardfulIntegration.vue'
  );
const ZoomIntegration = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/ZoomIntegration.vue'
  );
const GoogleCalendarIntegration = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/GoogleCalendarIntegration.vue'
  );
const GoogleMailIntegration = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/GoogleMailIntegration.vue'
  );
const GoogleMeetIntegration = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/GoogleMeetIntegration.vue'
  );
const InstagramIntegration = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/InstagramIntegration.vue'
  );
const ManageAdmins = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/ManageAdmins.vue'
  );
const CustomWaiver = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/CustomWaiver.vue'
  );
const Domain = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/Domain.vue'
  );
const LabelEditor = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/LabelEditor.vue'
  );
const TipJarIntegration = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/TipJarIntegration.vue'
  );

import ProductFormRoutes from './product-form-routes';
import QuizRoutes from './quiz-routes';
import { getStudentViewURL, getStudioURL } from './custom-domain-utils';
import PaymentPlans from '@/web/views/admin/PaymentPlans.vue';
import QuizSubmissionViewer from '@/web/views/admin/quizzes/QuizSubmissionViewer.vue';

const Calendar = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/events/Calendar.vue'
  );
const EventDetails = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/events/EventDetails.vue'
  );
const NewEvent = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/events/NewEvent.vue'
  );
const EditEvent = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/events/EditEvent.vue'
  );
const DuplicateEvent = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/events/DuplicateEvent.vue'
  );
const OpenTokLiveStream = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/events/streaming/OpenTokLiveStream.vue'
  );

// STUDIO / STUDENT VIEWS
const StudioTopBar = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/components/navigation/StudioTopBar.vue'
  );
const StudioFooter = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/components/navigation/StudioFooter.vue'
  );
const Studio404 = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/Studio404.vue'
  );
const StudioTemplate = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioTemplate.vue'
  );
const StudioHome = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioHome.vue'
  );
const StudentDashboard = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudentDashboard.vue'
  );
const StudentProducts = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudentProducts.vue'
  );
const StudentEvents = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudentEvents.vue'
  );
const StudioList = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioList.vue'
  );
const StudioMyFavorites = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioMyFavorites.vue'
  );
const StudioShop = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioShop.vue'
  );
const StudioGifts = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioGifts.vue'
  );
const StudioEventDetails = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioEventDetails.vue'
  );
const StudioEventSelectProduct = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioEventSelectProduct.vue'
  );
const StudioEventProductCheckout = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioEventProductCheckout.vue'
  );
const StudioCalendar = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioCalendar.vue'
  );
const StudioPodcasts = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioPodcasts.vue'
  );
const StudioPodcastDetails = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioPodcastDetails.vue'
  );
const StudioPodcastEpisodeDetails = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioPodcastEpisodeDetails.vue'
  );
const StudioCommunity = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioCommunity.vue'
  );
const StudioCommunityGroup = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioCommunityGroup.vue'
  );
const StudioCommunityFeed = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioCommunityFeed.vue'
  );
const StudioCommunityMembers = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioCommunityMembers.vue'
  );
const StudioCommunityGroupDescription = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioCommunityGroupDescription.vue'
  );
const StudioCommunityChat = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioCommunityChat.vue'
  );
const StudioProduct = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioProduct.vue'
  );
const StudioPlaylist = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioPlaylist.vue'
  );
const StudioMedia = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioMedia.vue'
  );
const StudioProductAbout = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/components/products/StudioProductAbout.vue'
  );
const StudioProductMedia = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioProductMedia.vue'
  );
const StudioPlaylistMedia = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioPlaylistMedia.vue'
  );
const StudioProductFreeMedia = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioProductFreeMedia.vue'
  );
const StudioProductFreeSample = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioProductFreeSample.vue'
  );
const StudioBundledProduct = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioBundledProduct.vue'
  );
const StudioProductIncludedProducts = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioProductIncludedProducts.vue'
  );
const StudioLessonDetail = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioLessonDetail.vue'
  );
const StudioProductContent = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/components/products/StudioProductContent.vue'
  );
const StudioProductPlaylists = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/components/products/StudioProductPlaylists.vue'
  );
const StudioProductLessons = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/components/products/StudioProductLessons.vue'
  );
const StudioProductEvents = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/components/products/StudioProductEvents.vue'
  );
const StudioProductCheckout = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioProductCheckout.vue'
  );
const StudioProductGiftCheckout = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioProductGiftCheckout.vue'
  );
const StudioGiftClaimInvitation = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioGiftClaim.vue'
  );
const StudioProductCheckoutPayPalConfirm = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioProductCheckoutPayPalConfirm.vue'
  );
const StudioProductCheckoutStripeConfirm = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioProductCheckoutStripeConfirm.vue'
  );
const StudioProductPrivateSessions = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/components/products/StudioProductPrivateSessions.vue'
  );
const StudioProductReviews = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/components/products/StudioProductReviews.vue'
  );
const StudioQuiz = () =>
  import(
    /* webpackChunkName: "group-studio-quiz" */ '@/web/views/shop/StudioQuiz.vue'
  );
const StudioOpenTokLiveStream = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioOpenTokLiveStream.vue'
  );
const StudioMeetingBooking = () =>
  import(
    /* webpackChunkName: "group-meetings" */ '@/web/views/shop/StudioMeetingBooking.vue'
  );
const StudioBookedMeeting = () =>
  import(
    /* webpackChunkName: "group-meetings" */ '@/web/views/shop/StudioBookedMeeting.vue'
  );
const StudioLogin = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioLogin.vue'
  );
const StudioSignup = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioSignup.vue'
  );
const StudioResetPassword = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioResetPassword.vue'
  );
const StudioGetResetPassword = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioGetResetPassword.vue'
  );
const StudioJoin = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioJoin.vue'
  );
const StudioOptIn = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioOptIn.vue'
  );
const StudioInvitation = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioInvitation.vue'
  );
const StudentCommunityProfileView = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudentCommunityProfileView.vue'
  );
const StudentSettings = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudentSettings.vue'
  );
const StudentCommunityProfileEdit = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudentCommunityProfileEdit.vue'
  );
const StudentPurchaseHistory = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudentPurchaseHistory.vue'
  );
const StudentRedemptions = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudentRedemptions.vue'
  );

const StudioMobileChat = () =>
  import(
    /* webpackChunkName: "group-mobile" */ '@/web/views/shop/StudioMobileChat.vue'
  );
const WidgetFullCalendar = () =>
  import(
    /* webpackChunkName: "group-widgets" */ '@/web/components/widgets/WidgetFullCalendar.vue'
  );
const WidgetCompactCalendar = () =>
  import(
    /* webpackChunkName: "group-widgets" */ '@/web/components/widgets/WidgetCompactCalendar.vue'
  );

const CompleteRegistration = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/CompleteRegistration.vue'
  );
const StudentAcceptWaiver = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudentAcceptWaiver.vue'
  );
const StudentSignContract = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/shop/StudentSignContract.vue'
  );
const StudioTips = () =>
  import(
    /* webpackChunkName: "group-studio" */ '@/web/views/shop/StudioTips.vue'
  );
const MarvelousBio = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/settings/MarvelousBio.vue'
  );
const StudentMarvelousBio = () =>
  import(
    /* webpackChunkName: "group-bio" */ '@/web/views/shop/StudentMarvelousBio.vue'
  );

const Community = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/community/Community.vue'
  );

const CommunityGroups = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/community/CommunityGroups.vue'
  );

const CommunityGroupDescription = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/community/CommunityGroupDescription.vue'
  );

const CommunityFlaggedComments = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/community/CommunityFlaggedComments.vue'
  );
const BioTemplate = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/bio/BioTemplate.vue'
  );
const BioPreview = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/bio/BioPreview.vue'
  );
const BioTemplates = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/bio/BioTemplates.vue'
  );
const BioDesign = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/bio/BioDesign.vue'
  );
const BioSettings = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/bio/BioSettings.vue'
  );
const BioShare = () =>
  import(
    /* webpackChunkName: "group-admin" */ '@/web/views/admin/bio/BioShare.vue'
  );

Vue.use(VueRouter);

const ADMIN_DOMAINS = [
  'app.namastream.com',
  'app.heymarvelous.com',
  'prodtest.heymarvelous.com',
  'app.heymarv.com',
  'beta.namastream.com',
  'demo.namastream.com',
  'my.namastream.com',
  'thrive.namastream.com',
  'localhost',
  '127.0.0.1',
];

const PRODUCTION_ADMIN_DOMAINS = [
  'app.namastream.com',
  'app.heymarvelous.com',
  'prodtest.heymarvelous.com',
];

function isAdminDomain() {
  return ADMIN_DOMAINS.includes(window.location.hostname.toLowerCase());
}

function isProductionAdminDomain() {
  return PRODUCTION_ADMIN_DOMAINS.includes(
    window.location.hostname.toLowerCase()
  );
}

function isLoggedIn() {
  return store.state.user.accessToken && store.state.user.role !== 'anonymous';
}

function isPageBuilderEnabled() {
  return store.state.user.teacher?.teacher?.is_page_builder_enabled;
}

function checkAffiliateRedirect() {
  return async function (to, from, next) {
    await store.state.user.userInfoPromise;
    const isExternalAffiliate = store.getters['user/isExternalAffiliate'];
    const isBioAccount = store.getters['user/isBioAccount'];
    if (isExternalAffiliate) {
      next('/share-and-earn');
    } else if (isBioAccount) {
      next('/bio');
    } else {
      next();
    }
  };
}

function checkPermissionForRoute(permission, checkFn?: () => boolean) {
  return async function (to, from, next) {
    if (
      (checkFn && checkFn()) ||
      (await store.dispatch('user/hasPermission', permission))
    ) {
      console.log('permission ' + permission + ' is granted');
      next();
    } else {
      console.log('no ' + permission + ' permissions');
      await store.dispatch('user/showUpgradeModal', true);
    }
  };
}

function checkHomePage() {
  return async function (to, from, next) {
    const homePage = undefined;
    if (homePage) {
      window.location.href = homePage;
    } else {
      next();
    }
  };
}

async function studioLoaded(store) {
  return new Promise((resolve, reject) => {
    const interval = setInterval(
      function () {
        if (store.state.studio?.currentStudio) {
          resolve(store.state.studio?.currentStudio);
          clearInterval(interval);
        }
      },
      200,
      50
    );
  });
}

function trackPageView(isStudio: boolean, path: string, studioURL: string) {
  if (isStudio) {
    studioLoaded(store).then(studio => {
      // @ts-ignore
      const studioFbPixelId = studio.fb_pixel_id;
      if (studioFbPixelId) trackFbPixel(studioFbPixelId);
      gaTrackPageView(path, studioURL);
    });
  } else {
    trackFbPixel(process.env.VUE_APP_FACEBOOK_PIXEL);
  }
}

function generateProductRoutes(prefix) {
  return [
    {
      path: 'about',
      meta: {
        isStudio: true,
        hideInstagramSlider: true,
        disableScrollToTop: true,
        hideMobileNav: true,
      },
      name: `${prefix}studio-product-about`,
      props: { review: Boolean(prefix) },
      component: StudioProductAbout,
    },
    {
      path: 'recordings',
      meta: {
        isStudio: true,
        hideInstagramSlider: true,
        disableScrollToTop: true,
        hideProductHeaderImage: true,
        hideMobileNav: true,
      },
      name: `${prefix}studio-product-content`,
      props: { review: Boolean(prefix) },
      component: StudioProductContent,
    },
    {
      path: 'playlists',
      meta: {
        isStudio: true,
        hideInstagramSlider: true,
        disableScrollToTop: true,
        hideProductHeaderImage: true,
        hideMobileNav: true,
      },
      name: `${prefix}studio-product-playlists`,
      props: { review: Boolean(prefix) },
      component: StudioProductPlaylists,
    },
    {
      path: 'free-sample',
      meta: {
        isStudio: true,
        hideInstagramSlider: true,
        disableScrollToTop: true,
        hideProductHeaderImage: true,
        hideMobileNav: true,
      },
      name: `${prefix}studio-product-free-sample`,
      props: { review: Boolean(prefix) },
      component: StudioProductFreeSample,
    },
    {
      path: 'included-products',
      meta: {
        isStudio: true,
        hideInstagramSlider: true,
        disableScrollToTop: true,
        hideProductHeaderImage: true,
        hideMobileNav: true,
      },
      name: `${prefix}bundle-included-content`,
      props: { review: Boolean(prefix) },
      component: StudioProductIncludedProducts,
    },
    {
      path: 'lessons',
      meta: {
        isStudio: true,
        hideInstagramSlider: true,
        disableScrollToTop: true,
        hideProductHeaderImage: true,
        hideMobileNav: true,
      },
      name: `${prefix}studio-product-lessons`,
      props: { review: Boolean(prefix) },
      component: StudioProductLessons,
    },
    {
      path: 'events',
      meta: {
        isStudio: true,
        hideInstagramSlider: true,
        disableScrollToTop: true,
        hideProductHeaderImage: true,
        hideMobileNav: true,
      },
      name: `${prefix}studio-product-events`,
      props: { review: Boolean(prefix) },
      component: StudioProductEvents,
    },
    {
      path: 'private-sessions',
      meta: {
        isStudio: true,
        hideInstagramSlider: true,
        disableScrollToTop: true,
        hideProductHeaderImage: true,
        hideMobileNav: true,
      },
      name: `${prefix}studio-product-private-sessions`,
      props: { review: Boolean(prefix) },
      component: StudioProductPrivateSessions,
    },
    {
      path: 'reviews',
      meta: {
        isStudio: true,
        hideInstagramSlider: true,
        disableScrollToTop: true,
        hideProductHeaderImage: true,
        hideMobileNav: true,
      },
      name: `${prefix}studio-product-reviews`,
      props: { review: Boolean(prefix) },
      component: StudioProductReviews,
    },
  ];
}

const routes: Array<RouteConfig> = [
  {
    path: '/login',
    name: 'admin-login',
    meta: {
      hideStudioContactInformation: true,
    },
    components: {
      default: AdminLogin,
      footer: StudioFooter,
    },
  },
  {
    path: '/signup',
    name: 'Signup',
    component: Signup,
    meta: {
      preventRefreshModal: true,
    },
  },
  {
    path: '/join',
    name: 'Join',
    component: Join,
    meta: {
      preventRefreshModal: true,
    },
  },
  {
    path: '/affiliate-signup',
    name: 'AffiliateSignup',
    component: AffiliateSignup,
    meta: {
      preventRefreshModal: true,
    },
  },
  {
    path: '/bio-signup',
    name: 'BioSignup',
    component: BioSignup,
    meta: {
      preventRefreshModal: true,
    },
  },
  {
    path: '/bio-upgrade',
    name: 'bio-upgrade',
    component: BioUpgrade,
    meta: {
      preventRefreshModal: true,
      requiresAuth: true,
    },
  },
  {
    path: '/code-signup',
    name: 'CodeSignup',
    meta: {
      preventRefreshModal: true,
    },
    component: CodeSignup,
  },
  {
    path: '/code-signup/appsumo',
    name: 'AppSumoCodeSignup',
    component: CodeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: { campaign: 'appsumo' },
  },
  {
    path: '/unsubscribe/:uuid/',
    name: 'Unsubscribe',
    component: Unsubscribe,
  },
  {
    path: '/reset-password/:uid/:token',
    name: 'admin-reset-password',
    component: AdminResetPassword,
  },
  {
    path: '/reset',
    name: 'admin-get-reset-password',
    component: AdminGetResetPassword,
  },
  {
    path: '/signup/sale',
    name: 'Sale',
    component: Signup,
    meta: {
      preventRefreshModal: true,
    },
    props: { sale: true },
  },
  {
    path: '/signup/innercircle',
    name: 'Innercircle Signup',
    component: Signup,
    meta: {
      preventRefreshModal: true,
    },
    props: { campaign: 'innercircle' },
  },
  {
    path: '/signup/yoga-alliance',
    name: 'Yoga-Alliance Signup',
    component: Signup,
    meta: {
      preventRefreshModal: true,
    },
    props: { campaign: 'yogaalliance' },
  },
  {
    path: '/signup/yoga-alliance-professionals',
    name: 'Yoga-Alliance-Professionals-Signup',
    component: Signup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      campaign: 'yap',
      affiliateRef: '41B2Y9DO',
    },
  },
  {
    path: '/signup/vstudio',
    name: 'VStudio-Signup',
    component: Signup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      campaign: 'vstudio',
      affiliateRef: 'ZLS3P9F0',
    },
  },
  {
    path: '/signup/lululemon',
    name: 'Lululemon Signup',
    component: Signup,
    meta: {
      preventRefreshModal: true,
    },
    props: { campaign: 'lululemon' },
  },
  {
    path: '/signup/inner-circle',
    name: 'ic-signup',
    component: InnerCircleProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      programmeCode: 'ic',
      title: 'Inner Circle',
      ctaButton: 'SIGNUP.BUTTON.JOIN_INNER_CIRCLE',
    },
  },
  {
    path: '/signup/inner-circle/nodf',
    name: 'ic-signup-nodf',
    component: InnerCircleProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      programmeCode: 'ic',
      title: 'Inner Circle',
      ctaButton: 'SIGNUP.BUTTON.JOIN_INNER_CIRCLE',
    },
  },
  {
    path: '/signup/inner-circle/sale',
    name: 'inner-circle-sale',
    component: InnerCircleProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      sale: true,
      programmeCode: 'ic',
      title: 'Inner Circle',
      ctaButton: 'SIGNUP.BUTTON.JOIN_INNER_CIRCLE',
    },
  },
  {
    path: '/signup/inner-circle-4',
    name: 'ic-signup',
    component: InnerCircle4MonthsProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      programmeCode: 'ic4',
      title: 'Inner Circle',
      ctaButton: 'SIGNUP.BUTTON.JOIN_INNER_CIRCLE',
    },
  },
  {
    path: '/signup/inner-circle-4/nodf',
    name: 'ic-signup-nodf',
    component: InnerCircle4MonthsProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      programmeCode: 'ic4',
      title: 'Inner Circle',
      ctaButton: 'SIGNUP.BUTTON.JOIN_INNER_CIRCLE',
    },
  },
  {
    path: '/signup/inner-circle-4/sale',
    name: 'inner-circle-sale',
    component: InnerCircle4MonthsProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      sale: true,
      programmeCode: 'ic4',
      title: 'Inner Circle',
      ctaButton: 'SIGNUP.BUTTON.JOIN_INNER_CIRCLE',
    },
  },
  {
    path: '/signup/launch-with-intention',
    name: 'lwi-signup',
    component: LWIProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      programmeCode: 'lwi',
      title: 'Launch with Intention',
      ctaButton: 'SIGNUP.BUTTON.JOIN_LAUNCH_WITH_INTENTION',
    },
  },
  {
    path: '/signup/launch-with-intention/nodf',
    name: 'lwi-signup-nodf',
    component: LWIProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      programmeCode: 'lwi',
      title: 'Launch with Intention',
      ctaButton: 'SIGNUP.BUTTON.JOIN_LAUNCH_WITH_INTENTION',
    },
  },
  {
    path: '/signup/launch-with-intention/sale',
    name: 'lwi-signup-sale',
    component: LWIProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      programmeCode: 'lwi',
      sale: true,
      title: 'Launch with Intention',
      ctaButton: 'SIGNUP.BUTTON.JOIN_LAUNCH_WITH_INTENTION',
    },
  },
  {
    path: '/signup/list-love',
    name: 'll-signup',
    component: ListLoveProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      programmeCode: 'll',
      title: 'List Love',
      ctaButton: 'SIGNUP.BUTTON.JOIN_LIST_LOVE',
    },
  },
  {
    path: '/signup/list-love/nodf',
    name: 'll-signup-nodf',
    component: ListLoveProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      programmeCode: 'll',
      title: 'List Love',
      ctaButton: 'SIGNUP.BUTTON.JOIN_LIST_LOVE',
    },
  },
  {
    path: '/signup/list-love/sale',
    name: 'll-signup-sale',
    component: ListLoveProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      programmeCode: 'll',
      sale: true,
      title: 'List Love',
      ctaButton: 'SIGNUP.BUTTON.JOIN_LIST_LOVE',
    },
  },
  {
    path: '/signup/marvelous-holiday-box',
    name: 'mhb-signup',
    component: MarvelousHolidayBoxProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      programmeCode: 'mhb',
      title: 'Marvelous Holiday Box',
      ctaButton: 'SIGNUP.BUTTON.GET_MARVELOUS_HOLIDAY_BOX',
    },
  },
  {
    path: '/signup/marvelous-holiday-box/nodf',
    name: 'mgb-signup-nodf',
    component: MarvelousHolidayBoxProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      programmeCode: 'mhb',
      title: 'Marvelous Holiday Box',
      ctaButton: 'SIGNUP.BUTTON.GET_MARVELOUS_HOLIDAY_BOX',
    },
  },
  {
    path: '/signup/marvelous-holiday-box/sale',
    name: 'mgb-signup-sale',
    component: MarvelousHolidayBoxProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      sale: true,
      programmeCode: 'mhb',
      title: 'Marvelous Holiday Box',
      ctaButton: 'SIGNUP.BUTTON.GET_MARVELOUS_HOLIDAY_BOX',
    },
  },
  {
    path: '/signup/membership-marvelous',
    name: 'lwi-signup',
    component: MembershipMarvelousProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      programmeCode: 'mm',
      title: 'Membership Marvelous',
      ctaButton: 'SIGNUP.BUTTON.JOIN_MEMBERSHIP_MARVELOUS',
    },
  },
  {
    path: '/signup/membership-marvelous/nodf',
    name: 'lwi-signup-nodf',
    component: MembershipMarvelousProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      programmeCode: 'mm',
      title: 'Membership Marvelous',
      ctaButton: 'SIGNUP.BUTTON.JOIN_MEMBERSHIP_MARVELOUS',
    },
  },
  {
    path: '/signup/membership-marvelous/sale',
    name: 'lwi-signup-sale',
    component: MembershipMarvelousProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      programmeCode: 'mm',
      sale: true,
      title: 'Membership Marvelous',
      ctaButton: 'SIGNUP.BUTTON.JOIN_MEMBERSHIP_MARVELOUS',
    },
  },
  {
    path: '/signup/challenge-funnel-blueprint',
    name: 'cfb-signup',
    component: ChallengeFunnelBlueprintProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      programmeCode: 'cfb',
      title: 'CHALLENGE FUNNEL BLUEPRINT',
      ctaButton: 'SIGNUP.BUTTON.JOIN_CHALLENGE_FUNNEL_BLUEPRINT',
    },
  },
  {
    path: '/signup/challenge-funnel-blueprint/nodf',
    name: 'cfb-signup',
    component: ChallengeFunnelBlueprintProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      programmeCode: 'cfb',
      title: 'CHALLENGE FUNNEL BLUEPRINT',
      ctaButton: 'SIGNUP.BUTTON.JOIN_CHALLENGE_FUNNEL_BLUEPRINT',
    },
  },
  {
    path: '/signup/challenge-funnel-blueprint/sale',
    name: 'cfb-signup',
    component: ChallengeFunnelBlueprintProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: {
      programmeCode: 'cfb',
      sale: true,
      title: 'CHALLENGE FUNNEL BLUEPRINT',
      ctaButton: 'SIGNUP.BUTTON.JOIN_CHALLENGE_FUNNEL_BLUEPRINT',
    },
  },
  {
    path: '/signup/inner-circle',
    name: 'ic-signup',
    component: ProgrammeSignup,
    meta: {
      preventRefreshModal: true,
    },
    props: { programmeCode: 'ic' },
  },
  {
    path: '/logout',
    name: 'logout',
    beforeEnter: (to, from, next) => {
      store.dispatch(`user/logout`);
      next({ name: 'admin-login' });
    },
  },
  {
    path: '/impersonate',
    name: 'impersonate',
    meta: {
      isImpersonate: true,
    },
    beforeEnter: async (to, from, next) => {
      await store.dispatch('user/loadUserInfo');
      const redirectData = await store.dispatch('user/getLoginRedirectData');
      next(redirectData);
    },
  },
  {
    path: '/magic',
    name: 'magic',
    meta: {
      isImpersonate: true,
    },
    beforeEnter: async (to, from, next) => {
      await store.dispatch('user/loadUserInfo');
      const redirectData = await store.dispatch('user/getLoginRedirectData');
      next(redirectData);
    },
  },
  {
    path: '/mobile-chat',
    name: 'mobile-chat',
    meta: {
      preventRefreshModal: true,
    },
    component: StudioMobileChat,
  },
  {
    path: '/',
    name: 'admin',
    meta: { requiresAuth: true },
    components: {
      sidebar: Sidebar,
      default: AdminTemplate,
    },
    redirect: { name: 'dashboard' },
    children: [
      {
        path: '/getstarted',
        name: 'getstarted',
        meta: { requiresAuth: true },
        component: QuickStartGuide,
      },
      {
        path: '/dashboard',
        name: 'dashboard',
        meta: { requiresAuth: true, passwordProtected: true },
        component: Dashboard,
        beforeEnter: checkAffiliateRedirect(),
      },
      {
        path: '/share-and-earn',
        name: 'share-and-earn',
        meta: { requiresAuth: true },
        component: ShareAndEarn,
      },
      {
        path: '/marketing/meeting-types',
        name: 'marketing-meeting-types',
        meta: { requiresAuth: true },
        component: MeetingTypes,
      },
      {
        path: '/marketing/meeting-types/:id/edit',
        name: 'marketing-meeting-type-edit',
        meta: { requiresAuth: true },
        component: MeetingTypeEditor,
      },
      {
        path: '/marketing/emails',
        name: 'marketing-emails',
        meta: { requiresAuth: true },
        component: Emails,
      },
      {
        path: '/marketing/emails/:id',
        name: 'marketing-email-detail',
        meta: { requiresAuth: true },
        component: EmailDetail,
      },
      {
        path: '/marketing/marketing-assets',
        name: 'marketing-assets',
        meta: { requiresAuth: true },
        component: MarketingAssets,
      },
      {
        path: '/marketing/announcements',
        name: 'marketing-announcement-detail',
        meta: { requiresAuth: true },
        component: AnnouncementDetail,
      },
      {
        path: '/media/library',
        name: 'media-library',
        meta: { requiresAuth: true },
        component: Library,
        beforeEnter: checkPermissionForRoute('has_media_library'),
      },
      {
        path: '/media/library/add',
        name: 'media-library-add',
        meta: { requiresAuth: true },
        component: LibraryAddMedia,
        beforeEnter: checkPermissionForRoute('has_media_library'),
      },
      {
        path: '/media/library/:media_id/edit',
        name: 'media-library-edit-item',
        meta: { requiresAuth: true },
        component: LibraryAddMedia,
        beforeEnter: checkPermissionForRoute('has_media_library'),
      },
      {
        path: '/media/library/:media_id',
        name: 'media-library-view-item',
        meta: { requiresAuth: true },
        component: LibraryViewMedia,
        beforeEnter: checkPermissionForRoute('has_media_library'),
      },
      {
        path: '/media/categories',
        name: 'media-categories',
        meta: { requiresAuth: true },
        component: Categories,
      },
      {
        path: '/media/instructors',
        name: 'media-instructors',
        meta: { requiresAuth: true },
        component: Instructors,
        beforeEnter: checkPermissionForRoute('has_additional_instructors'),
      },
      {
        path: '/events/calendar',
        name: 'events-calendar',
        meta: { requiresAuth: true },
        component: Calendar,
      },
      {
        path: '/events/:id/manage',
        name: 'events-details',
        meta: { requiresAuth: true },
        component: EventDetails,
      },
      {
        path: '/events/new/',
        name: 'events-new',
        meta: { requiresAuth: true },
        component: NewEvent,
      },
      {
        path: '/events/event/:id/edit',
        name: 'events-edit',
        meta: { requiresAuth: true },
        component: EditEvent,
      },
      {
        path: '/events/event/:id/duplicate',
        name: 'events-duplicate',
        meta: { requiresAuth: true },
        component: DuplicateEvent,
      },
      {
        path: '/events/event/:id/live',
        name: 'live-streaming',
        meta: {
          requiresAuth: true,
          hideAdminSidebar: true,
          hidePageBottomFiller: true,
          preventRefreshModal: true,
        },
        component: OpenTokLiveStream,
        beforeEnter: checkPermissionForRoute('has_tokbox_streaming'),
      },
      {
        path: '/reports',
        name: 'reports',
        meta: { requiresAuth: true, passwordProtected: true },
        component: Reports,
      },
      {
        path: '/reports/:group',
        name: 'report-group-list',
        meta: { requiresAuth: true },
        component: ReportListByGroup,
      },
      {
        path: '/reports/:group/:report_id',
        name: 'report-detail',
        meta: { requiresAuth: true },
        component: ReportDetail,
      },
      {
        path: '/pages',
        name: 'pages',
        meta: { requiresAuth: true },
        component: Pages,
        beforeEnter: checkPermissionForRoute('has_page_builder', () =>
          isPageBuilderEnabled()
        ),
      },
      {
        path: '/pages/edit/:id',
        name: 'edit-page',
        meta: { requiresAuth: true },
        beforeEnter: checkPermissionForRoute('has_page_builder', () =>
          isPageBuilderEnabled()
        ),
        component: EditPage,
        redirect: { name: 'page-setting' },
        children: [
          {
            path: 'settings',
            name: 'page-setting',
            component: PageSettings,
          },
          {
            path: 'seo',
            name: 'page-seo',
            component: PageSEO,
          },
        ],
      },
      {
        path: '/pages/add',
        name: 'add-page',
        meta: {
          requiresAuth: true,
          hideAdminSidebar: true,
          hidePageBottomFiller: true,
        },
        component: AddPage,
      },
      {
        path: '/pages/builder/:id',
        name: 'page-builder',
        meta: {
          requiresAuth: true,
          hideAdminSidebar: true,
          hidePageBottomFiller: true,
        },
        component: PageBuilder,
      },
      {
        path: '/elements',
        name: 'Elements',
        component: Elements,
      },
      {
        path: '/customers',
        name: 'CustomerList',
        meta: { requiresAuth: true, passwordProtected: true },
        component: CustomerList,
      },
      {
        path: '/customers/:id',
        name: 'CustomerDetail',
        meta: { requiresAuth: true },
        component: CustomerDetail,
        redirect: { name: 'customer-events' },
        children: [
          {
            path: 'activity',
            name: 'customer-activity',
            component: CustomerDetailActivity,
          },
          {
            path: 'class-card',
            name: 'customer-class-card',
            component: CustomerDetailClassCard,
          },
          {
            path: 'products',
            name: 'customer-products',
            component: CustomerDetailProducts,
          },
          {
            path: 'events',
            name: 'customer-events',
            component: CustomerDetailEvents,
          },
          {
            path: 'emails',
            name: 'customer-emails',
            component: CustomerDetailEmails,
          },
          {
            path: 'groups',
            name: 'customer-groups',
            component: CustomerDetailGroups,
          },
          {
            path: 'favorites',
            name: 'customer-favorites',
            component: CustomerDetailFavorites,
          },
          {
            path: 'waivers',
            name: 'customer-waivers',
            component: CustomerDetailWaivers,
          },
          {
            path: 'notes',
            name: 'customer-notes',
            component: CustomerDetailNotes,
          },
        ],
      },
      {
        path: '/customers/:customerId/notes/:id',
        name: 'CustomerEditNote',
        meta: { requiresAuth: true },
        component: CustomerEditNote,
      },
      {
        path: '/orders',
        name: 'Orders',
        meta: { requiresAuth: true, passwordProtected: true },
        component: Orders,
      },
      {
        path: '/payment-plans',
        name: 'payment-plans',
        meta: { requiresAuth: true, passwordProtected: true },
        component: PaymentPlans,
        beforeEnter: checkPermissionForRoute('has_payment_plans'),
      },
      {
        path: '/gift-purchases',
        name: 'gift-purchases',
        meta: { requiresAuth: true, passwordProtected: true },
        component: GiftPurchases,
      },
      {
        path: '/tip-jar',
        name: 'tip-jar',
        meta: { requiresAuth: true, passwordProtected: true },
        component: TipJar,
      },
      {
        path: '/settings',
        name: 'settings',
        meta: { requiresAuth: true, passwordProtected: true },
        component: Settings,
      },
      {
        path: '/settings/account',
        name: 'settings-account',
        meta: { requiresAuth: true, passwordProtected: true },
        component: Account,
      },
      {
        path: '/settings/branding',
        name: 'settings-branding',
        meta: { requiresAuth: true, passwordProtected: true },
        component: Branding,
      },
      {
        path: '/settings/adjust-plan',
        name: 'settings-adjust-plan',
        meta: { requiresAuth: true },
        component: AdjustPlan,
      },
      {
        path: '/settings/billing',
        name: 'settings-billing',
        meta: { requiresAuth: true, passwordProtected: true },
        component: Billing,
      },
      {
        path: '/settings/domain',
        name: 'settings-domain',
        meta: { requiresAuth: true, passwordProtected: true },
        component: Domain,
      },
      {
        path: '/settings/label-editor',
        name: 'settings-label-editor',
        meta: { requiresAuth: true, passwordProtected: true },
        component: LabelEditor,
        beforeEnter: checkPermissionForRoute('has_label_editor'),
      },
      {
        path: '/settings/custom-waiver',
        name: 'settings-integrations-custom-waiver',
        meta: { requiresAuth: true, passwordProtected: true },
        component: CustomWaiver,
        beforeEnter: checkPermissionForRoute('has_custom_waiver'),
      },
      {
        path: '/settings/widgets',
        name: 'settings-widgets',
        meta: { requiresAuth: true, passwordProtected: true },
        component: Widgets,
      },
      {
        path: '/settings/code-embed',
        name: 'settings-code-embed',
        meta: { requiresAuth: true, passwordProtected: true },
        component: CodeEmbed,
      },
      {
        path: '/settings/data-import',
        name: 'settings-data-import',
        meta: { requiresAuth: true, passwordProtected: true },
        component: DataImport,
        beforeEnter: checkPermissionForRoute('has_data_import'),
      },
      {
        path: '/settings/data-import/students',
        name: 'data-import-students',
        meta: { requiresAuth: true, passwordProtected: true },
        component: DataImportStudents,
      },
      {
        path: '/settings/data-import/media',
        name: 'data-import-media',
        meta: { requiresAuth: true, passwordProtected: true },
        component: DataImportMedia,
      },
      {
        path: '/settings/data-import/classcards',
        name: 'data-import-classcards',
        meta: { requiresAuth: true, passwordProtected: true },
        component: DataImportClassCards,
      },
      {
        path: '/settings/data-import/subscriptions',
        name: 'data-import-subscriptions',
        meta: { requiresAuth: true, passwordProtected: true },
        component: DataImportSubscriptions,
      },
      {
        path: '/settings/data-import/purchases',
        name: 'data-import-purchases',
        meta: { requiresAuth: true, passwordProtected: true },
        component: DataImportPurchases,
      },
      {
        path: '/settings/marvelous-bio',
        name: 'settings-marvelous-bio',
        meta: { requiresAuth: true, passwordProtected: true },
        component: MarvelousBio,
      },
      {
        path: '/settings/integrations',
        name: 'settings-integrations-integrations',
        meta: { requiresAuth: true, passwordProtected: true },
        component: Integrations,
      },
      {
        path: '/settings/integrations/calendly-old',
        name: 'settings-integrations-calendly-old',
        meta: { requiresAuth: true, passwordProtected: true },
        component: CalendlyIntegration,
        beforeEnter: checkPermissionForRoute('has_calendly'),
      },
      {
        path: '/settings/integrations/calendly',
        name: 'settings-integrations-calendly',
        meta: { requiresAuth: true, passwordProtected: true },
        component: CalendlyIntegrationV2,
        beforeEnter: checkPermissionForRoute('has_calendly'),
      },
      {
        path: '/settings/integrations/acuity',
        name: 'settings-integrations-acuity',
        meta: { requiresAuth: true, passwordProtected: true },
        component: AcuityIntegration,
        beforeEnter: checkPermissionForRoute('has_acuity'),
      },
      {
        path: '/settings/integrations/flodesk',
        name: 'settings-integrations-flodesk',
        meta: { requiresAuth: true, passwordProtected: true },
        component: FlodeskIntegration,
      },
      {
        path: '/settings/integrations/convertkit',
        name: 'settings-integrations-convertkit',
        meta: { requiresAuth: true, passwordProtected: true },
        component: ConvertkitIntegration,
      },
      {
        path: '/settings/integrations/mailchimp',
        name: 'settings-integrations-mailchimp',
        meta: { requiresAuth: true, passwordProtected: true },
        component: MailchimpIntegration,
      },
      {
        path: '/settings/integrations/twilio',
        name: 'settings-integrations-twilio',
        meta: { requiresAuth: true, passwordProtected: true },
        component: TwilioIntegration,
      },
      {
        path: '/settings/integrations/smtp',
        name: 'settings-integrations-smtp',
        meta: { requiresAuth: true, passwordProtected: true },
        component: SmtpIntegration,
      },
      {
        path: '/settings/integrations/paypal',
        name: 'settings-integrations-paypal',
        meta: { requiresAuth: true, passwordProtected: true },
        component: PayPalIntegration,
        beforeEnter: checkPermissionForRoute('has_paypal'),
      },
      {
        path: '/settings/integrations/stripe',
        name: 'settings-integrations-stripe',
        meta: { requiresAuth: true, passwordProtected: true },
        component: StripeIntegration,
      },
      {
        path: '/settings/integrations/facebook-pixel',
        name: 'settings-integrations-facebook-pixel',
        meta: { requiresAuth: true, passwordProtected: true },
        component: FacebookPixelIntegration,
      },
      {
        path: '/settings/integrations/google-analytics',
        name: 'settings-integrations-google-analytics',
        meta: { requiresAuth: true, passwordProtected: true },
        component: GoogleAnalyticsIntegration,
      },
      {
        path: '/settings/integrations/deadline-funnel',
        name: 'settings-integrations-deadline-funnel',
        meta: { requiresAuth: true, passwordProtected: true },
        component: DeadlineFunnelIntegration,
        beforeEnter: checkPermissionForRoute('has_deadline_funnel'),
      },
      {
        path: '/settings/integrations/zapier',
        name: 'settings-integrations-zapier',
        meta: { requiresAuth: true, passwordProtected: true },
        component: ZapierIntegration,
        beforeEnter: checkPermissionForRoute('has_zapier'),
      },
      {
        path: '/settings/integrations/rewardful',
        name: 'settings-integrations-rewardful',
        meta: { requiresAuth: true, passwordProtected: true },
        component: RewardfulIntegration,
      },
      {
        path: '/settings/integrations/zoom',
        name: 'settings-integrations-zoom',
        meta: { requiresAuth: true, passwordProtected: false },
        component: ZoomIntegration,
      },
      {
        path: '/settings/integrations/googlecalendar',
        name: 'settings-integrations-googlecalendar',
        meta: { requiresAuth: true, passwordProtected: false },
        component: GoogleCalendarIntegration,
      },
      {
        path: '/settings/integrations/googlemail',
        name: 'settings-integrations-googlemail',
        meta: { requiresAuth: true, passwordProtected: true },
        component: GoogleMailIntegration,
      },
      {
        path: '/settings/integrations/google-meet',
        name: 'settings-integrations-google-meet',
        meta: { requiresAuth: true, passwordProtected: false },
        component: GoogleMeetIntegration,
      },
      {
        path: '/settings/integrations/instagram',
        name: 'settings-integrations-instagram',
        meta: { requiresAuth: true, passwordProtected: true },
        component: InstagramIntegration,
      },
      {
        path: '/settings/admins',
        name: 'settings-admins',
        meta: { requiresAuth: true, passwordProtected: true },
        component: ManageAdmins,
        beforeEnter: checkPermissionForRoute('has_additional_admins'),
      },
      {
        path: '/settings/integrations/tip-jar',
        name: 'settings-integrations-tip-jar',
        meta: { requiresAuth: true, passwordProtected: true },
        component: TipJarIntegration,
      },
      {
        path: '/products/my-products',
        name: 'My products',
        meta: { requiresAuth: true },
        component: MyProduct,
      },
      {
        path: '/products/contracts',
        name: 'Contracts',
        meta: { requiresAuth: true, passwordProtected: true },
        component: Contracts,
      },
      {
        path: '/products/contracts/:id/edit',
        name: 'Edit contract',
        meta: { requiresAuth: true, passwordProtected: true },
        component: EditContract,
      },
      {
        path: '/products/podcasts',
        name: 'Podcasts',
        meta: {
          requiresAuth: true,
        },
        component: Podcasts,
      },
      {
        path: '/products/podcasts/:id/edit',
        name: 'Edit podcast',
        meta: {
          requiresAuth: true,
        },
        component: EditPodcast,
      },
      {
        path: '/products/podcasts/:id/episodes',
        name: 'Podcast episodes',
        meta: {
          requiresAuth: true,
        },
        component: PodcastEpisodes,
      },
      {
        path: '/products/coupons',
        name: 'Coupons',
        meta: { requiresAuth: true },
        component: Coupons,
      },
      {
        path: '/products/coupons/:id/edit',
        name: 'Edit coupon',
        meta: { requiresAuth: true },
        component: EditCoupon,
      },
      {
        path: '/products/coupons/create',
        name: 'Create coupon',
        meta: { requiresAuth: true },
        component: CreateCoupon,
      },
      {
        path: '/products/coupons/:id',
        name: 'Coupon detail',
        meta: { requiresAuth: true },
        component: CouponDetail,
      },
      {
        path: '/products/product-tags',
        name: 'product-tags',
        meta: { requiresAuth: true },
        component: ProductTags,
      },
      {
        path: '/products/tax-rates',
        name: 'tax-rates',
        meta: { requiresAuth: true },
        component: TaxRates,
        beforeEnter: checkPermissionForRoute('has_taxes'),
      },
      {
        path: '/products/quizzes',
        name: 'quizzes',
        meta: { requiresAuth: true },
        component: Quizzes,
        beforeEnter: checkPermissionForRoute('has_quizzes'),
      },
      {
        path: '/products/:id',
        name: 'Product detail',
        meta: { requiresAuth: true },
        component: ProductDetail,
      },
      {
        path: '/products/:id/edit',
        name: 'Edit product',
        meta: {
          requiresAuth: true,
          hideAdminSidebar: true,
        },
        component: EditProduct,
        children: ProductFormRoutes,
      },
      {
        path: '/products/quizzes/:id',
        name: 'quiz',
        meta: {
          requiresAuth: true,
          hideAdminSidebar: true,
          preventRefreshModal: true,
        },
        redirect: {
          name: 'quiz-overview',
        },
        component: Quiz,
        props: true,
        children: QuizRoutes,
        beforeEnter: checkPermissionForRoute('has_quizzes'),
      },
      {
        name: 'quiz-submission-grader',
        path: '/products/quizzes/:id/submissions/:submission_id/grade',
        component: QuizSubmissionViewer,
        meta: {
          requiresAuth: true,
          hideAdminSidebar: true,
          preventRefreshModal: true,
        },
        beforeEnter: checkPermissionForRoute('has_quizzes'),
      },
      {
        name: 'quiz-submission-viewer',
        path: '/products/quizzes/:id/submissions/:submission_id/view',
        component: QuizSubmissionViewer,
        meta: {
          requiresAuth: true,
          hideAdminSidebar: true,
        },
        beforeEnter: checkPermissionForRoute('has_quizzes'),
      },
      {
        path: '/community',
        name: 'community',
        meta: { requiresAuth: true },
        component: Community,
      },
      {
        path: '/communitygroups',
        name: 'community-groups',
        meta: { requiresAuth: true },
        component: CommunityGroups,
      },
      {
        path: '/communitygroups/:group_id/description',
        name: 'community-group-description',
        meta: { requiresAuth: true },
        component: CommunityGroupDescription,
      },
      {
        path: '/community/comments/flagged',
        name: 'community-flagged-comments',
        meta: { requiresAuth: true },
        component: CommunityFlaggedComments,
      },
    ],
  },
  {
    path: '/bio',
    name: 'bio',
    meta: { requiresAuth: true },
    components: {
      topbar: BioTopBar,
      default: BioTemplate,
    },
    redirect: { name: 'bio-link' },
    children: [
      {
        path: 'links',
        name: 'bio-link',
        components: {
          preview: BioPreview,
          default: BioTemplates,
        },
      },
      {
        path: 'design',
        name: 'bio-design',
        components: {
          preview: BioPreview,
          default: BioDesign,
        },
      },
      {
        path: 'settings',
        name: 'bio-settings',
        components: {
          preview: BioPreview,
          default: BioSettings,
        },
      },
      {
        path: 'share',
        name: 'bio-share',
        components: {
          preview: BioPreview,
          default: BioShare,
        },
      },
      {
        path: 'upgrade',
        name: 'upgrade',
        component: AdjustPlan,
        meta: {
          hideUpgradeBanner: true,
        },
        props: {
          showNav: false,
        },
      },
    ],
  },
  {
    path: '/thank-you',
    name: 'thank-you',
    meta: {
      requiresAuth: true,
      preventRefreshModal: true,
    },
    components: {
      default: ThankYou,
    },
  },
  {
    path: '/thank-you-bio',
    name: 'thank-you-bio',
    meta: {
      requiresAuth: true,
      preventRefreshModal: true,
    },
    components: {
      default: ThankYouBio,
    },
  },
  {
    path: '/:studio_url/streaming/:event_id/live',
    name: 'studio-live-stream',
    meta: {
      isStudio: true,
      hidePageBottomFiller: true,
      customDomainPath: '/streaming/:event_id/live',
      preventRefreshModal: true,
      requiresAuth: true,
    },
    components: {
      default: StudioOpenTokLiveStream,
    },
  },
  {
    path: '/404',
    meta: { isStudio: true },
    name: 'studio-404',
    components: {
      topbar: StudioTopBar,
      default: Studio404,
      footer: StudioFooter,
    },
  },
  {
    path: '/:studio_url',
    name: 'studio-home-page',
    meta: {
      isStudio: true,
      customDomainPath: '/',
    },
    components: {
      topbar: StudioTopBar,
      default: StudioTemplate,
      footer: StudioFooter,
    },
    redirect: { name: 'studio-home' },
    children: [
      {
        path: '/',
        meta: { isStudio: true },
        name: 'studio-home',
        component: StudioHome,
        beforeEnter: checkHomePage(),
      },
      {
        path: 'meet/:meeting_type_slug',
        name: 'studio-book-meeting',
        meta: {
          isStudio: true,
          hideMobileNav: true,
          hideTopBar: true,
          hideInstagramSlider: true,
          hideStudioContactInformation: true,
          hideCookieConsent: true,
          hideStudioAdminFooter: true,
          preventRefreshModal: true,
        },
        component: StudioMeetingBooking,
      },
      {
        path: 'meeting/:meeting_unique_id',
        name: 'studio-booked-meeting',
        meta: {
          isStudio: true,
          hideMobileNav: true,
          hideTopBar: true,
          hideInstagramSlider: true,
          hideStudioContactInformation: true,
          hideCookieConsent: true,
          hideStudioAdminFooter: true,
          preventRefreshModal: true,
        },
        component: StudioBookedMeeting,
      },
      {
        path: 'bio',
        name: 'marvelous-bio',
        meta: {
          isStudio: true,
          hideTopBar: true,
          hideInstagramSlider: true,
          hideStudioContactInformation: true,
          hideCookieConsent: true,
          hideStudioAdminFooter: true,
          preventRefreshModal: true,
        },
        component: StudentMarvelousBio,
      },
      {
        path: 'dashboard',
        meta: {
          isStudio: true,
          requiresAuth: true,
          hideInstagramSlider: true,
          showStudentNavigation: true,
        },
        name: 'studio-dashboard',
        component: StudentDashboard,
      },
      {
        path: 'products',
        meta: {
          isStudio: true,
          requiresAuth: true,
          hideInstagramSlider: true,
          showStudentNavigation: true,
        },
        name: 'studio-products',
        component: StudentProducts,
      },
      {
        path: 'events',
        meta: {
          isStudio: true,
          requiresAuth: true,
          hideInstagramSlider: true,
          showStudentNavigation: true,
        },
        name: 'studio-events',
        component: StudentEvents,
      },
      {
        path: 'shop',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
        },
        name: 'studio-shop',
        component: StudioShop,
      },
      {
        path: 'gifts',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
        },
        name: 'studio-gifts',
        component: StudioGifts,
      },
      {
        path: 'gifts/:uuid',
        name: 'studio-gifts-accept',
        meta: {
          isStudio: true,
          hideTopBar: true,
          hideInstagramSlider: true,
          hideStudioContactInformation: true,
          hideStudioAdminFooter: true,
          preventRefreshModal: true,
          requiresAuth: false,
        },
        component: StudioGiftClaimInvitation,
      },
      {
        path: 'join',
        meta: { isStudio: true },
        name: 'studio-join',
        components: {
          topbar: StudioTopBar,
          default: StudioJoin,
          footer: StudioFooter,
        },
      },
      {
        path: 'optin',
        name: 'StudioOptIn',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          requiresAuth: true,
          hideMobileNav: true,
        },
        components: {
          topbar: StudioTopBar,
          default: StudioOptIn,
          footer: StudioFooter,
        },
      },
      {
        path: 'product/:product_id/media/:media_id',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          requiresAuth: true,
          hideMobileNav: true,
        },
        name: 'studio-media',
        component: StudioMedia,
      },
      {
        path: 'bundle/:bundle_id/product/:product_id',
        meta: {
          isStudio: true,
          hideMobileNav: true,
        },
        name: 'studio-bundled-product',
        component: StudioBundledProduct,
        children: generateProductRoutes('bundle-'),
      },
      {
        path: 'calendar',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
        },
        name: 'studio-calendar',
        component: StudioCalendar,
      },
      {
        path: 'podcasts',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          showStudentNavigation: true,
        },
        name: 'studio-podcasts',
        component: StudioPodcasts,
      },
      {
        path: 'podcasts/:podcast_id',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          showStudentNavigation: true,
        },
        name: 'studio-podcast-details',
        component: StudioPodcastDetails,
      },
      {
        path: 'podcasts/:podcast_id/episodes/:episode_id',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          showStudentNavigation: true,
        },
        name: 'studio-podcast-episode-details',
        component: StudioPodcastEpisodeDetails,
      },
      {
        path: 'community',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          showStudentNavigation: true,
        },
        name: 'studio-community',
        component: StudioCommunity,
      },
      {
        path: 'community-groups/:group_id',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          requiresAuth: true,
        },
        name: 'studio-community-group',
        redirect: {
          name: 'studio-community-feed',
        },
        component: StudioCommunityGroup,
        children: [
          {
            path: 'feed',
            meta: {
              isStudio: true,
              hideInstagramSlider: true,
              disableScrollToTop: true,
              requiresAuth: true,
            },
            name: 'studio-community-feed',
            component: StudioCommunityFeed,
          },
          {
            path: 'members',
            meta: {
              isStudio: true,
              hideInstagramSlider: true,
              disableScrollToTop: true,
              requiresAuth: true,
            },
            name: 'studio-community-members',
            component: StudioCommunityMembers,
          },
          {
            path: 'about',
            meta: {
              isStudio: true,
              hideInstagramSlider: true,
              disableScrollToTop: true,
              requiresAuth: true,
            },
            name: 'studio-community-group-description',
            component: StudioCommunityGroupDescription,
          },
        ],
      },
      {
        path: 'chat',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          hideStudioContactInformation: true,
          requiresAuth: true,
        },
        name: 'studio-community-chat',
        component: StudioCommunityChat,
      },
      {
        path: 'my-studios',
        meta: {
          isStudio: true,
          requiresAuth: true,
          ignoreStudioExpiration: true,
          hideInstagramSlider: true,
        },
        name: 'studio-list',
        component: StudioList,
      },
      {
        path: 'my-favorites',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          requiresAuth: true,
          showStudentNavigation: true,
        },
        name: 'studio-my-favorites',
        component: StudioMyFavorites,
      },
      {
        path: 'community-profile/:id',
        name: 'student-community-profile-view',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          requiresAuth: true,
        },
        component: StudentCommunityProfileView,
      },
      {
        path: 'profile',
        name: 'student-settings',
        meta: {
          isStudio: true,
          requiresAuth: true,
          ignoreStudioExpiration: true,
          hideInstagramSlider: true,
          hideStudioContactInformation: true,
        },
        component: StudentSettings,
      },
      {
        path: 'community-profile-edit',
        name: 'student-community-profile-edit',
        meta: {
          isStudio: true,
          requiresAuth: true,
          hideStudioContactInformation: false,
        },
        component: StudentCommunityProfileEdit,
      },
      {
        path: 'profile/purchases',
        name: 'student-purchase-history',
        meta: {
          isStudio: true,
          requiresAuth: true,
          ignoreStudioExpiration: true,
          hideStudioContactInformation: true,
        },
        component: StudentPurchaseHistory,
      },
      {
        path: 'profile/redemptions',
        name: 'student-redemptions',
        meta: {
          isStudio: true,
          requiresAuth: true,
          ignoreStudioExpiration: true,
          hideInstagramSlider: true,
          hideStudioContactInformation: true,
        },
        component: StudentRedemptions,
      },
      {
        path: 'buy/product/:product_id',
        name: 'studio-product-checkout',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          hideTopBar: true,
          hideStudioContactInformation: true,
          preventRefreshModal: true,
          hideMobileNav: true,
          hideCookieNotice: true,
        },
        component: StudioProductCheckout,
      },
      {
        path: 'gift/product/:product_id',
        name: 'studio-product-gift',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          hideTopBar: true,
          hideStudioContactInformation: true,
          preventRefreshModal: true,
          hideMobileNav: true,
          hideCookieNotice: true,
        },
        component: StudioProductGiftCheckout,
      },
      {
        path: 'gift/invitation/:invitation_id',
        name: 'studio-gift-claim-invitation',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          hideTopBar: true,
          hideStudioContactInformation: true,
          preventRefreshModal: true,
          hideMobileNav: true,
        },
        component: StudioGiftClaimInvitation,
      },
      {
        path: 'buy/product/:product_id/paypal/confirm',
        name: 'studio-product-checkout-paypal-confirm',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          preventRefreshModal: true,
        },
        component: StudioProductCheckoutPayPalConfirm,
      },
      {
        path: 'buy/product/:product_id/stripe/confirm',
        name: 'studio-product-checkout-stripe-confirm',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          preventRefreshModal: true,
        },
        component: StudioProductCheckoutStripeConfirm,
      },
      {
        path: 'product/:product_id/playlists/:playlist_id',
        name: 'studio-playlist',
        meta: { isStudio: true },
        components: {
          topbar: StudioTopBar,
          default: StudioPlaylist,
          footer: StudioFooter,
        },
      },
      {
        path: 'product/:product_id/playlists/:playlist_id/media/:media_id',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          requiresAuth: true,
        },
        name: 'studio-playlist-media',
        component: StudioPlaylistMedia,
      },
      {
        path: 'product/:product_id',
        name: 'studio-product',
        meta: { isStudio: true },
        components: {
          topbar: StudioTopBar,
          default: StudioProduct,
          footer: StudioFooter,
        },
        children: generateProductRoutes(''),
      },
      {
        path: 'product/:product_id/media/:media_id',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          requiresAuth: true,
        },
        name: 'studio-product-media',
        component: StudioProductMedia,
      },
      {
        path: 'product/:product_id/free-media/:media_id',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
        },
        name: 'studio-product-free-media',
        component: StudioProductFreeMedia,
      },
      {
        path: 'product/:product_id/lesson/:lesson_id/play/:media_id',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          requiresAuth: true,
        },
        name: 'studio-lesson-media',
        component: StudioLessonDetail,
      },
      {
        path: 'product/:product_id/lesson/:lesson_id/view',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
        },
        name: 'studio-lesson-non-media',
        component: StudioLessonDetail,
      },
      {
        path: 'event/details/:event_id',
        name: 'studio-event-details',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
        },
        components: {
          default: StudioEventDetails,
        },
      },
      {
        path: 'event/details/:event_id/select-product',
        name: 'studio-event-select-product',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          hideTopBar: true,
          hideStudioContactInformation: true,
          hideMobileNav: true,
        },
        components: {
          default: StudioEventSelectProduct,
        },
      },
      {
        path: 'event/details/:event_id/product/:product_id/checkout',
        name: 'studio-event-product-checkout',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          hideTopBar: true,
          hideStudioContactInformation: true,
          preventRefreshModal: true,
          hideMobileNav: true,
          hideCookieNotice: true,
        },
        components: {
          default: StudioEventProductCheckout,
        },
      },
      {
        path: 'login',
        meta: { isStudio: true, ignoreStudioExpiration: true },
        name: 'studio-login',
        component: StudioLogin,
        beforeEnter: async (to, from, next) => {
          // redirect to dashboard, if user lands on the login page and is already logged in
          if (isLoggedIn()) {
            next({ name: 'studio-dashboard', params: to.params });
          } else {
            next();
          }
        },
      },
      {
        path: 'signup',
        meta: { isStudio: true, ignoreStudioExpiration: true },
        name: 'studio-signup',
        component: StudioSignup,
      },
      {
        path: 'complete-registration/:uuid/',
        name: 'complete-registration',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
        },
        component: CompleteRegistration,
      },
      {
        path: 'logout',
        name: 'studio-logout',
        beforeEnter: (to, from, next) => {
          store.dispatch(`user/logout`);
          next({ name: 'studio-home' });
        },
      },
      {
        path: 'reset-password/:uid/:token',
        meta: {
          isStudio: true,
          ignoreStudioExpiration: true,
          hideInstagramSlider: true,
        },
        name: 'studio-reset-password',
        component: StudioResetPassword,
      },
      {
        path: 'reset',
        meta: {
          isStudio: true,
          ignoreStudioExpiration: true,
          hideInstagramSlider: true,
        },
        name: 'studio-get-reset-password',
        component: StudioGetResetPassword,
      },
      {
        path: 'waiver',
        name: 'sign-waiver',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          requiresAuth: true,
        },
        component: StudentAcceptWaiver,
      },
      {
        path: 'waiver/:uuid',
        name: 'sign-waiver-uuid',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          hideMobileNav: true,
          hideTopBar: true,
          hideStudioContactInformation: true,
        },
        component: StudentAcceptWaiver,
      },
      {
        path: 'contracts/:unique_id',
        name: 'studio-sign-contract',
        meta: {
          isStudio: true,
          requiresAuth: true,
          hideInstagramSlider: true,
        },
        component: StudentSignContract,
      },
      {
        path: 'invitation/:invitation_unique_id',
        meta: {
          isStudio: true,
          ignoreStudioExpiration: false,
          hideInstagramSlider: true,
        },
        name: 'studio-invitation',
        component: StudioInvitation,
      },
      {
        path: 'quiz/:quiz_id',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          hideTopBar: true,
          hideStudioContactInformation: true,
          preventRefreshModal: true,
          requiresAuth: true,
          hideMobileNav: true,
        },
        name: 'studio-quiz',
        component: StudioQuiz,
      },
      {
        path: 'tips',
        meta: {
          isStudio: true,
          hideInstagramSlider: true,
          hideTopBar: true,
          hideStudioContactInformation: true,
          preventRefreshModal: true,
          hideMobileNav: true,
        },
        name: 'studio-tips',
        component: StudioTips,
      },
      {
        path: 'widgets/calendars/full',
        meta: {
          isStudio: true,
          hideTopBar: true,
          hideInstagramSlider: true,
          hideStudioContactInformation: true,
          hideCookieConsent: true,
          hideStudioAdminFooter: true,
          preventRefreshModal: true,
          preventScroll: true,
          hideMobileNav: true,
        },
        name: 'widget-full-calendar',
        component: WidgetFullCalendar,
      },
      {
        path: 'widgets/calendars/compact',
        meta: {
          isStudio: true,
          hideTopBar: true,
          hideInstagramSlider: true,
          hideStudioContactInformation: true,
          hideCookieConsent: true,
          hideStudioAdminFooter: true,
          preventRefreshModal: true,
          preventScroll: true,
          hideMobileNav: true,
        },
        name: 'widget-compact-calendar',
        component: WidgetCompactCalendar,
      },
    ],
  },
  {
    path: '*',
    meta: { isStudio: true },
    beforeEnter: async (to, from, next) => {
      console.log('router wildcard route triggered ', to, from);
      const storedStudioURL = store.getters['globals/studioUrl'] || '';
      console.log('storedStudioURL=', storedStudioURL);
      const fullPath = to.fullPath || '';
      const regexp = new RegExp(`^/${storedStudioURL}/`, 'i');
      const regexpWithoutSlash = new RegExp(`^/${storedStudioURL}`, 'i');
      if (fullPath.length && fullPath.match(regexp)) {
        let replacedPath = fullPath.replace(regexp, '/');
        if (replacedPath.length === 0) replacedPath = '/';
        console.log('replaced path=', replacedPath);
        next(replacedPath);
      } else if (fullPath.length && fullPath.match(regexpWithoutSlash)) {
        let replacedPath = fullPath.replace(regexpWithoutSlash, '');
        if (replacedPath.length === 0) replacedPath = '/';
        console.log('replaced path=', replacedPath);
        next(replacedPath);
      } else {
        console.log('fullpath not matched =', fullPath);
        next({ name: 'studio-dashboard' }); // leave it to beforeEach to select most suitable route
      }
    },
  },
];

async function getRoutesDynamically() {
  const isSubdomainsServer =
    process.env.VUE_APP_IS_SUBDOMAINS_SERVER === 'true';
  if (isAdminDomain() && !isSubdomainsServer) {
    return routes;
  } else {
    try {
      if (isSubdomainsServer) {
        const studioUrl = window.location.hostname.split('.')[0];
        await store.dispatch('globals/setStudioUrl', studioUrl);
      } else {
        const studio = await AuthApi.getStudioByCustomDomain(
          window.location.hostname
        );
        await store.dispatch('globals/setStudioUrl', studio.studio_url);
        console.log('matched custom domain: ', studio.studio_url);
      }
      return routes
        .filter(route => route.meta?.isStudio)
        .map(route => {
          const newRoute = {
            ...route,
            path: route.meta?.customDomainPath || route.path,
          };
          return newRoute;
        });
    } catch (error) {
      return routes;
    }
  }
}

function scrollBehavior(to, from, savedPosition) {
  if (to.hash) {
    setTimeout(() => {
      const element = document.getElementById(to.hash.replace(/#/, ''));
      if (element && element.scrollIntoView) {
        element.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }, 500);
    return { selector: to.hash };
  }
  if (to.meta.disableScrollToTop) {
    return null;
  }
  return { x: 0, y: 0 };
}

async function getRouter(): Promise<VueRouter> {
  const preparedRoutes = await getRoutesDynamically();

  const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    scrollBehavior: scrollBehavior,
    routes: preparedRoutes,
  });

  router.beforeEach(async (to, from, next) => {
    const isStudioPage = to.matched.some(record => record.meta.isStudio);

    //console.log('router to=', to)
    if (process.env.VUE_APP_STUDIO_ON_SUBDOMAINS === 'true') {
      if (isProductionAdminDomain() && isStudioPage) {
        const studioUrl = to.params.studio_url;
        const url = new URL(window.location.href);
        const domain =
          url.hostname.split('.').splice(1).join('.') || 'heymarvelous.com';
        const redirectUrl = `https://${studioUrl}.${domain}${to.fullPath}`;
        console.log('Redirecting to subdomain', redirectUrl);
        // @ts-ignore
        window.location = redirectUrl;
      }
    }

    const mapTo = replaceAngularUrls(to);
    if (mapTo) {
      next(mapTo);
      return;
    }

    // Identify Mixpanel and remove param from URL
    const mixpanelDistinctId = to.query.distinct_id;
    if (mixpanelDistinctId) {
      const newQuery = { ...to.query };
      delete newQuery.distinct_id;
      identify({ mixpanelDistinctId });
      next({ path: to.path, query: newQuery });
    }
    if (store.state.globals.isMainNavOpen) {
      store.dispatch('globals/openMainNav', false);
    }

    const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
    let accessToken = store.state.user.accessToken;
    const isImpersonate = to.matched.some(record => record.meta.isImpersonate);

    // handle magic_link code
    const magicCode = to.query.magic_code;
    //const canOverrideCurrentLogin = !accessToken || isImpersonate
    const canOverrideCurrentLogin = true;
    if (magicCode && canOverrideCurrentLogin) {
      try {
        const loginResponse = await store.dispatch(
          'user/loginWithMagicCode',
          magicCode
        );
        await store.dispatch('user/loadUserInfo');
        accessToken = store.state.user.accessToken;
        delete to.query.magic_code;
        // @ts-ignore
        next({ ...to, replace: true });
      } catch (e) {
        console.error('magic code is invalid or expired!');
      }
    }

    let loadUserInfoPromise = store.state.user.userInfoPromise;

    // if this route requires auth
    if (requiresAuth) {
      // if access token
      if (accessToken) {
        // if loadUserInfo didn't yet started
        if (!loadUserInfoPromise) {
          loadUserInfoPromise = await store.dispatch('user/loadUserInfo');
        }
        await loadUserInfoPromise;
        if (!isLoggedIn()) {
          return next({
            name: 'admin-login',
            query: {
              ...to.query,
              redirect: to.fullPath,
            },
          });
        }
        const lastStudioUrl = store.getters['user/studentsLastStudioUrl'];
        const studioURL = getStudioURL(from) || lastStudioUrl;
        const role = store.getters['user/role'];
        if (role === 'teacher' || isStudioPage) next();
        // proceed if possible
        else next({ name: 'studio-home', params: { studio_url: studioURL } }); // redirect if user doesn't have access
      } else {
        // redirect if no access token
        if (isStudioPage) {
          await store.dispatch('globals/setShowLoginModal', true);
          next();
          // next({
          //   name: 'studio-home',
          //   params: { studio_url: getStudioURL(to) },
          // });
        } else {
          next({
            name: 'admin-login',
            query: {
              ...to.query,
              redirect: to.fullPath,
            },
          });
        }
      }
    } else return next(); // if this route doesn't require auth
  });

  router.afterEach(to => {
    const isStudioPage = to.matched.some(record => record.meta.isStudio);
    const name = to.name;
    if (name && !isStudioPage) {
      track(`teacher page view - ${name}`);
    }
    trackPageView(isStudioPage, to.path, getStudioURL(to));
  });

  return router;
}

export default getRouter;
